import { useEffect, useState } from 'react';
import { HelpCircle, ListTodo, Loader2, Sparkles, Star } from 'lucide-react';
import { catchUpFooter, type CatchUp } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useCanAnywhere } from '@/lib/permissions';
import { useAiStatus } from '@/lib/translate';
import { Button, ErrorBox, Modal, Tabs } from '@/components/ui';
import { MessageBody } from '@/features/chat/MessageBody';

type Window = 'unread' | 'day' | 'week';
const since = (w: Window) => (w === 'unread' ? null : new Date(Date.now() - (w === 'day' ? 1 : 7) * 86_400_000).toISOString());

/** Markdown-ish bullets from the model (or basic mode) rendered with the normal inline formatter. */
export function Bullets({ text }: { text: string }) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  return (
    <div className="space-y-1 text-sm leading-relaxed">
      {lines.map((l, i) => l.startsWith('- ') || l.startsWith('* ')
        ? <div key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" /><span><MessageBody body={l.slice(2)} /></span></div>
        : <p key={i}><MessageBody body={l} /></p>)}
    </div>
  );
}

export function useCanCatchUp() { return useCanAnywhere('ai.catch_up.use'); }

/** "Catch me up": summary of what you missed in a channel (since last read by default) or of one thread. */
export function CatchUpModal({ conversationId, parentId, title, onClose, onJump }: { conversationId: string; parentId?: string | null; title: string; onClose: () => void; onJump?: (messageId: string) => void }) {
  const ws = useWorkspaceId();
  const status = useAiStatus();
  const [win, setWin] = useState<Window>('unread');
  const [data, setData] = useState<CatchUp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ws) return;
    let live = true;
    setLoading(true); setError(null);
    api.ai.catchUp(ws, { conversation_id: conversationId, parent_id: parentId ?? null, since: parentId ? null : since(win) })
      .then(r => { if (live) setData(r); }).catch(e => { if (live) setError(errorMessage(e)); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [ws, conversationId, parentId, win]);

  const jump = (id: string) => { onJump?.(id); onClose(); };
  const range = data && `${new Date(data.since).toLocaleString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} – now`;

  return (
    <Modal open onClose={onClose} wide title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent-fg" />{parentId ? 'Thread summary' : `Catch me up · ${title}`}</span>}
      footer={<div className="flex w-full items-center justify-between text-xs text-slate-500">
        <span data-testid="catch-up-footer">{loading ? null : catchUpFooter(data, status.data?.model)}</span>
        <Button variant="ghost" onClick={onClose}>Close</Button></div>}>
      {!parentId && <Tabs className="-mt-2 mb-4" value={win} onChange={setWin} tabs={[{ id: 'unread', label: 'Since I last read' }, { id: 'day', label: 'Last 24 hours' }, { id: 'week', label: 'Last 7 days' }]} />}
      <ErrorBox error={error} />
      {loading && <div className="flex items-center gap-2 py-10 text-sm text-slate-500 justify-center"><Loader2 className="h-4 w-4 animate-spin" />Reading the conversation…</div>}
      {!loading && data && (
        <div className="space-y-5" data-testid="catch-up">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span><b className="text-slate-700 dark:text-slate-200">{data.message_count}</b> message{data.message_count === 1 ? '' : 's'}</span>
            {range && !parentId && <span>{range}</span>}
            {data.mentions_me > 0 && <span className="font-medium text-amber-800 dark:text-amber-200">You were mentioned {data.mentions_me}×</span>}
            {data.people.length > 0 && <span>From {data.people.slice(0, 4).map(p => p.name.split(' ')[0]).join(', ')}{data.people.length > 4 ? ` +${data.people.length - 4}` : ''}</span>}
          </div>
          {data.notice && <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800">{data.notice}</div>}
          {data.message_count === 0 ? <p className="py-6 text-center text-sm text-slate-500">Nothing new – you're all caught up.</p> : <Bullets text={data.summary} />}
          {data.highlights.length > 0 && (
            <section>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Star className="h-3.5 w-3.5" />Worth reading</h4>
              <ul className="space-y-1.5">{data.highlights.map(h => (
                <li key={h.message_id}><button onClick={() => jump(h.message_id)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-sm hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900">
                  <div className="text-xs text-slate-500"><b className="text-slate-700 dark:text-slate-200">{h.author_name}</b> · {h.reason}</div>
                  <div className="line-clamp-2"><MessageBody body={h.text} /></div></button></li>))}</ul>
            </section>
          )}
          {data.action_items.length > 0 && (
            <section>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><ListTodo className="h-3.5 w-3.5" />Asks and actions</h4>
              <ul className="space-y-1 text-sm">{data.action_items.map((a, i) => (
                <li key={i} className="flex gap-2"><span className="mt-1.5 h-3 w-3 shrink-0 rounded border border-slate-300" />
                  <span>{a.text}{a.owner_name && <span className="text-slate-500"> – {a.owner_name}</span>}{a.message_id && <button className="ml-1 text-xs font-semibold text-accent-fg hover:underline" onClick={() => jump(a.message_id!)}>View</button>}</span></li>))}</ul>
            </section>
          )}
          {data.open_questions.length > 0 && (
            <section>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><HelpCircle className="h-3.5 w-3.5" />Still unanswered</h4>
              <ul className="space-y-1 text-sm">{data.open_questions.map(q => (
                <li key={q.message_id}><button onClick={() => jump(q.message_id)} className="text-left hover:underline"><b>{q.author_name}:</b> <MessageBody body={q.text} /></button></li>))}</ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
